import { SortingState } from "@tanstack/react-table";
import { isEqual } from "lodash";
import type { CompareToSelection } from "@/entities/dataTables/compareTo/types";
import type { ColumnData } from "@/entities/dataTables/types";

interface UseDisplaySettingsChangedParams {
  groupBy?: string[];
  sorting?: SortingState;
  defaultSortField?: string;
  /** Omitted by tables with no Compare-to control, and then never a change. */
  compareTo?: CompareToSelection;
  defaultCompareTo?: CompareToSelection;
  columnsState?: ColumnData[];
  defaultColumnsState?: ColumnData[];
}

interface UseDisplaySettingsChangedResult {
  currentSortField: string;
  currentSortDesc: boolean;
  hasGroupByChanged: boolean;
  hasSortFieldChanged: boolean;
  hasSortDirectionChanged: boolean;
  hasCompareToChanged: boolean;
  hasColumnsChanged: boolean;
  hasChanges: boolean;
}

/** Same period, and for a custom range the same start instant. */
const isSameSelection = (
  selection: CompareToSelection | undefined,
  otherSelection: CompareToSelection | undefined
): boolean => {
  if (!selection || !otherSelection) return selection === otherSelection;
  if (selection.period !== otherSelection.period) return false;
  return (
    selection.customStart?.getTime() === otherSelection.customStart?.getTime()
  );
};

// The table renders columnsState in array order and hides any column it omits,
// so what the user sees is exactly the ordered list of visible ids.
const visibleColumnIds = (columns: ColumnData[]): string[] =>
  columns.filter((column) => column.isVisible).map((column) => column.id);

export const useDisplaySettingsChanged = ({
  groupBy = [],
  sorting = [],
  defaultSortField = "name",
  compareTo,
  defaultCompareTo,
  columnsState = [],
  defaultColumnsState = []
}: UseDisplaySettingsChangedParams): UseDisplaySettingsChangedResult => {
  const currentSortField = sorting?.[0]?.id || "";
  const currentSortDesc = sorting?.[0]?.desc ?? false;

  const hasGroupByChanged = groupBy.length > 0;
  const hasSortFieldChanged = currentSortField !== defaultSortField;
  const hasSortDirectionChanged = currentSortDesc === true; // Default is ascending (false)

  // Without this a timeframe-only change leaves Reset disabled — so the reset
  // path that clears it is unreachable — and the Display button shows no
  // changed-state indicator for a setting the user did change.
  //
  // `defaultCompareTo` is required for the same reason DisplayPopover requires
  // it before rendering the control: with no default there is nothing to
  // compare against and nothing for Reset to restore, so an incomplete
  // configuration would report a permanent change for a control the user
  // cannot see or clear.
  const hasCompareToChanged =
    !!compareTo &&
    !!defaultCompareTo &&
    !isSameSelection(compareTo, defaultCompareTo);

  // An empty columnsState means the table has not been seeded and shows its
  // defaults; with no defaults there is nothing for Reset to restore.
  const hasColumnsChanged =
    columnsState.length > 0 &&
    defaultColumnsState.length > 0 &&
    !isEqual(
      visibleColumnIds(columnsState),
      visibleColumnIds(defaultColumnsState)
    );

  const hasChanges =
    hasGroupByChanged ||
    hasSortFieldChanged ||
    hasSortDirectionChanged ||
    hasCompareToChanged ||
    hasColumnsChanged;

  return {
    currentSortField,
    currentSortDesc,
    hasGroupByChanged,
    hasSortFieldChanged,
    hasSortDirectionChanged,
    hasCompareToChanged,
    hasColumnsChanged,
    hasChanges
  };
};
